import { useEffect, useState } from "react";
import { Lock, Key, Monitor, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function PasswordStrength({ pwd }) {
  const rules = [
    { test: pwd.length >= 8, label: "At least 8 characters" },
    { test: /[A-Z]/.test(pwd), label: "One uppercase letter" },
    { test: /[0-9]/.test(pwd), label: "One number" },
    { test: /[^A-Za-z0-9]/.test(pwd), label: "One symbol" },
  ];
  const score = rules.filter((r) => r.test).length;
  const color = score >= 3 ? "bg-emerald-500" : score >= 2 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="mt-2">
      <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${(score / 4) * 100}%` }} />
      </div>
      <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500">
        {rules.map((r) => (
          <li key={r.label} className={r.test ? "text-emerald-700" : "text-slate-500"}>
            {r.test ? "✓" : "·"} {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Security() {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/me/sessions");
        setSession(data.current);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const change = async () => {
    if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
    if (form.new_password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post("/users/me/password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="security-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
          Account
        </div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Security</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage your password and review recent sessions.
        </p>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-emerald-600" />
            <h3 className="font-semibold">Change password</h3>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            You'll stay signed in on this device after changing your password.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label>Current password</Label>
              <PasswordInput
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                className="mt-1.5 h-11"
                data-testid="sec-current-password"
                testIdToggle="sec-current-password-toggle"
              />
            </div>
            <div>
              <Label>New password</Label>
              <PasswordInput
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="mt-1.5 h-11"
                data-testid="sec-new-password"
                testIdToggle="sec-new-password-toggle"
              />
              {form.new_password && <PasswordStrength pwd={form.new_password} />}
            </div>
            <div>
              <Label>Confirm new password</Label>
              <PasswordInput
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="mt-1.5 h-11"
                data-testid="sec-confirm-password"
                testIdToggle="sec-confirm-password-toggle"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={change}
                disabled={busy || !form.current_password || !form.new_password}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="sec-change-btn"
              >
                <Lock className="h-4 w-4 mr-1" /> {busy ? "Updating…" : "Update password"}
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4 text-slate-600" />
              <h3 className="font-semibold">Current session</h3>
            </div>
            {session ? (
              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">IP</span>
                  <span className="font-mono text-slate-700">{session.ip}</span>
                </div>
                <div>
                  <div className="text-slate-500 text-[10px] uppercase tracking-wider">
                    User agent
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-slate-700 break-all">
                    {session.user_agent}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 text-xs text-slate-500">Loading…</div>
            )}
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex items-center gap-2 text-amber-900">
              <ShieldAlert className="h-4 w-4" />
              <div className="font-semibold text-sm">Security tips</div>
            </div>
            <ul className="mt-3 space-y-1.5 text-xs text-amber-900/80">
              <li>· Use a unique password you don't reuse elsewhere.</li>
              <li>· Rotate passwords every 90 days.</li>
              <li>· Treat invitation links as confidential.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
